import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/apiClient';
import { getErrorMessage } from '../utils/errorMessage';
import { useNavigate } from 'react-router-dom';
import Menu from '../components/Menu';
import NotificationContainer from '../components/NotificationContainer';
import { useTheme } from '@mui/material/styles';
import { useNotifications } from '../context/NotificationContext';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Button,
  Alert,
  Divider,
  Chip,
  Card,
  CardContent,
  Grid,
  Autocomplete,
  TextField
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import SecurityIcon from '@mui/icons-material/Security';
import { normalizeRole, getRoleLabel, ASSIGNABLE_ROLES } from '../utils/roles';

export default function AccessControl() {
  const { t } = useTranslation('admin');
  const theme = useTheme();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotifications();

  // Определяем права доступа по категориям
  const permissions = {
    devices_access: {
      title: t('accessCategoryDevicesAccess'),
      permissions: [
        { id: 'view_other_devices', label: t('accessPermViewOtherDevices'), description: t('accessPermViewOtherDevicesDesc') },
        { id: 'user_device_send_command', label: t('accessPermSendCommand'), description: t('accessPermSendCommandDesc') },
        { id: 'divece_assign_owner', label: t('accessPermAssignOwner'), description: t('accessPermAssignOwnerDesc') },
        { id: 'divece_change_owner', label: t('accessPermChangeOwner'), description: t('accessPermChangeOwnerDesc') },
        { id: 'divece_change_info', label: t('accessPermChangeInfo'), description: t('accessPermChangeInfoDesc') },
        { id: 'divece_change_hosting', label: t('accessPermChangeHosting'), description: t('accessPermChangeHostingDesc') },
        { id: 'device_delete', label: t('accessPermDeviceDelete'), description: t('accessPermDeviceDeleteDesc') },
        { id: 'device_create', label: t('accessPermDeviceCreate'), description: t('accessPermDeviceCreateDesc') },
        { id: 'management_location', label: t('accessPermManageLocation'), description: t('accessPermManageLocationDesc') },
        { id: 'device_change_location', label: t('accessPermChangeLocation'), description: t('accessPermChangeLocationDesc') }
      ]
    },
    devices_control: {
      title: t('accessCategoryDevicesControl'),
      permissions: [
        { id: 'user_device_management_restart', label: t('accessPermRestart'), description: t('accessPermRestartDesc') },
        { id: 'user_device_management_change_pool', label: t('accessPermChangePool'), description: t('accessPermChangePoolDesc') },
        { id: 'user_device_management_hardreset', label: t('accessPermHardreset'), description: t('accessPermHardresetDesc') },
        { id: 'user_device_management_chipmap', label: t('accessPermChipmap'), description: t('accessPermChipmapDesc') },
        { id: 'user_device_management_log', label: t('accessPermLog'), description: t('accessPermLogDesc') },
        { id: 'user_device_management_led', label: t('accessPermLed'), description: t('accessPermLedDesc') },
        { id: 'agent_action', label: t('accessPermAgentAction'), description: t('accessPermAgentActionDesc') },
        { id: 'change_status', label: t('accessPermChangeStatus'), description: t('accessPermChangeStatusDesc') }
      ]
    },
    users_roles: {
      title: t('accessCategoryUsersRoles'),
      permissions: [
        { id: 'delete_user', label: t('accessPermDeleteUser'), description: t('accessPermDeleteUserDesc') },
        { id: 'change_user', label: t('accessPermChangeUser'), description: t('accessPermChangeUserDesc') },
        { id: 'edit_role_promote_to_manager', label: t('accessPermPromoteManager'), description: t('accessPermPromoteManagerDesc') },
        { id: 'edit_role_promote_to_admin', label: t('accessPermPromoteAdmin'), description: t('accessPermPromoteAdminDesc') },
        { id: 'Role_cannot_be_changed', label: t('accessPermRoleCannotChange'), description: t('accessPermRoleCannotChangeDesc') },
        { id: 'view_personal_information', label: t('accessPermViewPersonalInfo'), description: t('accessPermViewPersonalInfoDesc') }
      ]
    },
    messaging: {
      title: t('accessCategoryMessaging'),
      permissions: [
        { id: 'send_message_to_user', label: t('accessPermMsgToUser'), description: t('accessPermMsgToUserDesc') },
        { id: 'send_message_to_hosting', label: t('accessPermMsgToHosting'), description: t('accessPermMsgToHostingDesc') },
        { id: 'send_message_to_admin', label: t('accessPermMsgToAdmin'), description: t('accessPermMsgToAdminDesc') },
        { id: 'send_message_to_all', label: t('accessPermMsgToAll'), description: t('accessPermMsgToAllDesc') },
        { id: 'fast_notification', label: t('accessPermFastNotification'), description: t('accessPermFastNotificationDesc') }
      ]
    },
    billing: {
      title: t('accessCategoryBilling'),
      permissions: [
        { id: 'bill_edit', label: t('accessPermBillEdit'), description: t('accessPermBillEditDesc') }
      ]
    },
    settings: {
      title: t('accessCategorySettings'),
      permissions: [
        { id: 'models-settings', label: t('accessPermModelsSettings'), description: t('accessPermModelsSettingsDesc') }
      ]
    },
    misc: {
      title: t('accessCategoryMisc'),
      permissions: [
        { id: 'inviolable', label: t('accessPermInviolable'), description: t('accessPermInviolableDesc') }
      ]
    }
  };
  const [selectedUser, setSelectedUser] = useState('');
  const [users, setUsers] = useState([]);
  const [userPermissions, setUserPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [allHostings, setAllHostings] = useState([]);
  const [viewedRole, setViewedRole] = useState('');
  const [assignedHostings, setAssignedHostings] = useState([]);
  const [savingRoleHostings, setSavingRoleHostings] = useState(false);

  // Загрузка пользователей
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('users');

      if (response.data.users) {
        setUsers(response.data.users);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Ошибка при загрузке пользователей:', error);
      const msg = getErrorMessage(error, t('accessErrorLoadUsers'));
      setError(msg);
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  const buildEmptyPermissions = () => {
    const empty = {};
    Object.keys(permissions).forEach(category => {
      permissions[category].permissions.forEach(permission => {
        empty[permission.id] = false;
      });
    });
    return empty;
  };

  // Загрузка прав пользователя
  const fetchUserPermissions = async (userId) => {
    if (!userId) return;

    try {
      const response = await api.get(`access-control/${userId}`);

      const raw = response.data;
      const payload = raw?.access_control ?? raw?.permissions ?? raw?.data ?? raw;

      if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        // объект вида { permissionId: boolean }
        setUserPermissions(payload);
      } else if (Array.isArray(payload)) {
        // массив permissionId
        const mapped = buildEmptyPermissions();
        payload.forEach((pid) => { if (pid in mapped) mapped[pid] = true; });
        setUserPermissions(mapped);
      } else {
        setUserPermissions(buildEmptyPermissions());
      }
    } catch (error) {
      console.error('Ошибка при загрузке прав доступа:', error);
      setUserPermissions(buildEmptyPermissions());
      showError(getErrorMessage(error, t('accessErrorLoadPermissions')));
    }
  };

  // Загрузка списка всех хостингов (один раз)
  const fetchAllHostings = async () => {
    try {
      const resp = await api.get('data/hosting');
      const list = Array.isArray(resp?.data?.results) ? resp.data.results : [];
      setAllHostings(list);
    } catch (e) {
      console.error('Ошибка при загрузке списка хостингов:', e);
      setAllHostings([]);
    }
  };

  // Загрузка хостингов привязанных к юзеру
  const fetchAssignedHostings = async (userId) => {
    if (!userId) return;
    try {
      const resp = await api.get(`access-control/${userId}/hostings`);
      const ids = Array.isArray(resp?.data?.hosting_ids) ? resp.data.hosting_ids : [];
      setAssignedHostings(ids);
    } catch (e) {
      console.error('Ошибка при загрузке хостингов юзера:', e);
      setAssignedHostings([]);
    }
  };

  // Загружаем данные при монтировании компонента
  useEffect(() => {
    fetchUsers();
    fetchAllHostings();
  }, []);

  // Загружаем права при изменении выбранного пользователя
  useEffect(() => {
    if (selectedUser) {
      fetchUserPermissions(selectedUser);
      fetchAssignedHostings(selectedUser);
      const u = users.find(x => x.id === selectedUser);
      const normalized = normalizeRole(u?.role) || 'client';
      setViewedRole(normalized);
    } else {
      setAssignedHostings([]);
      setViewedRole('');
    }
  }, [selectedUser, users]);

  // Обработчик изменения прав
  const handlePermissionChange = (permissionId) => {
    setUserPermissions(prev => ({
      ...prev,
      [permissionId]: !prev[permissionId]
    }));
  };

  // Обработчик изменения всех прав в категории
  const handleCategoryChange = (category, value) => {
    const newPermissions = { ...userPermissions };
    permissions[category].permissions.forEach(permission => {
      newPermissions[permission.id] = value;
    });
    setUserPermissions(newPermissions);
  };

  // Сохранение роли и привязанных площадок
  const handleSaveRoleHostings = async () => {
    if (!selectedUser) return;
    try {
      setSavingRoleHostings(true);
      setError(null);

      // 1. Роль
      await api.post(`access-control/role/${selectedUser}`, { role: viewedRole });

      // 2. Хостинги. Для hoster пишем выбранный список, для остальных всегда чистим,
      // иначе при откате hoster -> client/admin привязки оставались бы стейл.
      const ids = viewedRole === 'hoster' ? assignedHostings : [];
      await api.patch(`access-control/${selectedUser}/hostings`, { hosting_ids: ids });

      showSuccess(t('accessRoleHostingsSaved'));
      // Обновляем локальный список юзеров чтобы chip роли в селекте обновился
      await fetchUsers();
      await fetchAssignedHostings(selectedUser);
    } catch (e) {
      console.error('Ошибка при сохранении роли и хостингов:', e);
      const msg = getErrorMessage(e, t('accessRoleHostingsError'));
      setError(msg);
      showError(msg);
    } finally {
      setSavingRoleHostings(false);
    }
  };

  // Сохранение прав пользователя
  const handleSavePermissions = async () => {
    if (!selectedUser) {
      setError(t('accessErrorUserNotSelected'));
      showError(t('accessErrorUserNotSelected'));
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = { permissions: userPermissions };
      await api.put(`access-control/${selectedUser}`, payload);

      setSuccess(t('accessSuccessSavePermissions'));
      setTimeout(() => setSuccess(null), 3000);
      showSuccess(t('accessSuccessSavePermissions'));
    } catch (error) {
      console.error('Ошибка при сохранении прав:', error);
      const msg = getErrorMessage(error, t('accessErrorSavePermissions'));
      setError(msg);
      showError(msg);
    } finally {
      setSaving(false);
    }
  };

  // Получение имени пользователя по ID
  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return t('accessUnknownUser');
    if (user.name && user.email) {
      return `${user.name} (${user.email})`;
    }
    return user.name || user.email || t('accessUnknownUser');
  };

  // Проверка, выбраны ли все права в категории
  const isCategoryFullySelected = (category) => {
    const categoryPermissions = permissions[category].permissions;
    return categoryPermissions.every(permission => userPermissions[permission.id]);
  };

  // Проверка, выбрана ли часть прав в категории
  const isCategoryPartiallySelected = (category) => {
    const categoryPermissions = permissions[category].permissions;
    const selectedCount = categoryPermissions.filter(permission => userPermissions[permission.id]).length;
    return selectedCount > 0 && selectedCount < categoryPermissions.length;
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
        <Menu />
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <Typography variant="h6">{t('accessLoadingUsers')}</Typography>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
      <Menu />
      <div style={{ padding: '20px' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            <SecurityIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            {t('accessTitle')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('accessSubtitle')}
          </Typography>
        </Box>

        {/* Выбор пользователя */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <FormControl sx={{ minWidth: 300 }}>
              <InputLabel>{t('accessSelectUser')}</InputLabel>
              <Select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                label={t('accessSelectUser')}
              >
                {users.map((user) => {
                  const normalized = normalizeRole(user.role);
                  const chipColor = normalized === 'admin' || normalized === 'super_admin'
                    ? 'error'
                    : normalized === 'hoster'
                      ? 'warning'
                      : 'default';
                  return (
                    <MenuItem key={user.id} value={user.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={getRoleLabel(user.role, t) || 'client'}
                          size="small"
                          color={chipColor}
                        />
                        {user.name ? `${user.name}${user.email ? ` (${user.email})` : ''}` : user.email}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchUsers}
              disabled={loading}
            >
              {t('accessRefresh')}
            </Button>
          </Box>
          
          {selectedUser && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('accessSettingRightsFor')}<strong>{getUserName(selectedUser)}</strong>
            </Alert>
          )}
        </Paper>

        {/* Роль и привязанные площадки */}
        {selectedUser && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('accessRoleAndHostings')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { md: 'center' }, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: { xs: '100%', md: 240 } }}>
                <InputLabel>{t('accessRoleLabel')}</InputLabel>
                <Select
                  value={viewedRole}
                  label={t('accessRoleLabel')}
                  onChange={(e) => setViewedRole(e.target.value)}
                >
                  {ASSIGNABLE_ROLES.map(r => (
                    <MenuItem key={r} value={r}>{getRoleLabel(r, t)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {viewedRole === 'hoster' && (
                <Autocomplete
                  multiple
                  options={allHostings}
                  value={allHostings.filter(h => assignedHostings.includes(h.id))}
                  onChange={(_, newValue) => setAssignedHostings(newValue.map(h => h.id))}
                  getOptionLabel={(o) => o?.name || ''}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  disableCloseOnSelect
                  sx={{ flex: 1, minWidth: { xs: '100%', md: 320 } }}
                  renderInput={(params) => (
                    <TextField {...params} label={t('accessHostingsLabel')} placeholder={t('accessHostingsPlaceholder')} variant="outlined" />
                  )}
                />
              )}
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveRoleHostings}
                disabled={savingRoleHostings}
                sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}
              >
                {savingRoleHostings ? t('accessSaving') : t('accessSaveRoleHostings')}
              </Button>
            </Box>
            {viewedRole === 'hoster' && assignedHostings.length === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t('accessHostingsEmptyWarning')}
              </Alert>
            )}
          </Paper>
        )}

        {/* Показываем ошибку если есть */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Показываем успех если есть */}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Таблица прав */}
        {selectedUser && (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                {t('accessRightsFor')}{getUserName(selectedUser)}
              </Typography>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSavePermissions}
                disabled={saving}
              >
                {saving ? t('accessSaving') : t('accessSaveRights')}
              </Button>
            </Box>

            <Grid container spacing={3}>
              {Object.keys(permissions).map((category) => (
                <Grid item xs={12} md={6} key={category}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" component="h3">
                          {permissions[category].title}
                        </Typography>
                        <Checkbox
                          checked={isCategoryFullySelected(category)}
                          indeterminate={isCategoryPartiallySelected(category)}
                          onChange={(e) => handleCategoryChange(category, e.target.checked)}
                          size="small"
                        />
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {permissions[category].permissions.map((permission) => (
                          <Box key={permission.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Checkbox
                              checked={userPermissions[permission.id] || false}
                              onChange={() => handlePermissionChange(permission.id)}
                              size="small"
                            />
                            <Box>
                              <Typography variant="body2" component="div">
                                {permission.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {permission.description}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Сообщение если пользователь не выбран */}
        {!selectedUser && !loading && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <SecurityIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('accessUserNotSelectedTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('accessUserNotSelectedSubtitle')}
            </Typography>
          </Paper>
        )}
      </div>
      <NotificationContainer />
    </div>
  );
}

